import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { PageLayoutBodyScrollEvent } from '@usepepper/sprout-kit-ui';
import { useCallback, useEffect, useRef } from 'react';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';

const HIDE_DELTA_PX = 6;
const REVEAL_DELTA_PX = 6;
const TOP_REVEAL_OFFSET_PX = 4;

interface ScrollMetrics {
  contentHeight: number;
  offsetY: number;
  viewportHeight: number;
}

type ScrollHandler = (input: PageLayoutBodyScrollEvent | ScrollMetrics) => void;

// Used to differentiate from the synthetic events sent by Salsa,
// and real scroll events on web or RN
const isScrollEvent = (
  input: PageLayoutBodyScrollEvent | ScrollMetrics,
): input is PageLayoutBodyScrollEvent => 'nativeEvent' in input;

const isNativeScroll = (
  event: PageLayoutBodyScrollEvent,
): event is NativeSyntheticEvent<NativeScrollEvent> =>
  'contentOffset' in event.nativeEvent;

/**
 * Normalizes a scroll input to raw metrics: bridged metrics pass through;
 * RN and web scroll events are extracted.
 *
 * @param input - The scroll event, or already-normalized metrics.
 * @returns The normalized scroll metrics:
 * - `contentHeight` — Total height of the scrollable content.
 * - `offsetY` — Current vertical scroll offset from the top.
 * - `viewportHeight` — Height of the visible scroll viewport.
 */
const getScrollMetrics = (
  input: PageLayoutBodyScrollEvent | ScrollMetrics,
): ScrollMetrics => {
  if (!isScrollEvent(input)) {
    return input;
  }

  if (isNativeScroll(input)) {
    const { contentOffset, contentSize, layoutMeasurement } = input.nativeEvent;
    return {
      contentHeight: contentSize.height,
      offsetY: contentOffset.y,
      viewportHeight: layoutMeasurement.height,
    };
  }

  const { scrollHeight, scrollTop, clientHeight } = input.currentTarget;
  return {
    contentHeight: scrollHeight,
    offsetY: scrollTop,
    viewportHeight: clientHeight,
  };
};

/**
 * Drives the bottom nav's on scroll behavior.
 *
 * The bottom nav will hide on scroll down, or display on scroll up. Taking into
 * account iOS's rubber-band effect which reverses your scroll when the end is reached.
 *
 * @returns A scroll handler callback, which takes a scroll event and uses it
 *    hide/show the bottom nav.
 */
export function useBottomNavScrollHandler(): ScrollHandler {
  const lastOffsetYRef = useRef(0);

  // Reset the ExperienceUIStore on unmount
  useEffect(
    () => () => useExperienceUIStore.getState().setBottomNavHidden(false),
    [],
  );

  return useCallback((input) => {
    const {
      contentHeight,
      offsetY: rawOffsetY,
      viewportHeight,
    } = getScrollMetrics(input);
    const { isBottomNavHidden, setBottomNavHidden } =
      useExperienceUIStore.getState();

    const maxOffsetY = Math.max(0, contentHeight - viewportHeight);
    const offsetY = Math.min(Math.max(0, rawOffsetY), maxOffsetY);
    const delta = offsetY - lastOffsetYRef.current;
    lastOffsetYRef.current = offsetY;

    // Show the bottom nav when the user scrolls up.
    // (Also protect against the iOS rubber-band effect, when users overscroll)
    if (offsetY <= TOP_REVEAL_OFFSET_PX || delta <= -REVEAL_DELTA_PX) {
      if (isBottomNavHidden) {
        setBottomNavHidden(false);
      }
    } else if (delta >= HIDE_DELTA_PX && !isBottomNavHidden) {
      setBottomNavHidden(true);
    }
  }, []);
}
